import type React from "react";
import { type FC, type ReactNode } from "react";

interface InputProps {
  type?: "text" | "number" | "email" | "password" | "date" | "time" | string;
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  min?: string;
  max?: string;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  success?: boolean;
  error?: boolean;
  hint?: string;
  autoFocus?: boolean;
  /** Which keyboard a phone shows — `decimal` for an amount. */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const Input: FC<InputProps> = ({
  type = "text",
  id,
  name,
  placeholder,
  value,
  onChange,
  onBlur,
  className = "",
  min,
  max,
  step,
  required = false,
  disabled = false,
  success = false,
  error = false,
  hint,
  autoFocus = false,
  inputMode,
  startIcon,
  endIcon,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedby,
}) => {
  // Nothing here touches `outline`: the global :focus-visible ring is the focus
  // indicator, rather than the 1.3:1 glow this used to draw over a killed one.
  let inputClasses = `field-base h-11 appearance-none py-2.5 ${
    startIcon ? "pl-11" : "pl-4"
  } ${endIcon ? "pr-11" : "pr-4"} ${className}`;

  if (disabled) {
    inputClasses += " field-disabled border-border-subtle";
  } else if (error) {
    inputClasses += " field-invalid";
  } else if (success) {
    inputClasses += " field-valid";
  } else {
    inputClasses += " field-default";
  }

  return (
    <div className="w-full">
      {/* The icons are centred on the field alone, not on the field and its hint. */}
      <div className="relative">
        {startIcon && (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-tertiary z-10 pointer-events-none">
            {startIcon}
          </span>
        )}
        <input
          type={type}
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={inputClasses}
          required={required}
          autoFocus={autoFocus}
          inputMode={inputMode}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedby}
        />
        {endIcon && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-content-tertiary z-10 pointer-events-none">
            {endIcon}
          </span>
        )}
      </div>

      {hint && (
        <p
          className={`mt-1.5 text-xs ${error
            ? "text-error-500"
            : success
              ? "text-success-500"
              : "text-gray-500"
            }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
};

export default Input;
